import React, { useEffect, useRef } from 'react';
import { nfConfirm } from './confirm';

const DEFAULT_CLOSE_CONFIRM = 'Введённые данные не сохранятся.';

/**
 * confirmOverlayClose — форма с вводом (true или свой текст вопроса): если в ней
 * что-то вводили, клик в подложку спрашивает подтверждение, а обновление/закрытие
 * вкладки браузера вызывает штатное предупреждение. Крестик закрывает без вопроса.
 * dirty — изменения, которые не дают событий ввода (добавили позицию, «+/−» количества):
 * форма сообщает о них сама.
 */
function Modal({ title, children, onClose, footer, size = 'default', confirmOverlayClose, dirty: dirtyProp = false }) {
  const modalClass = size === 'xl' ? 'modal modal-xl' : size === 'large' ? 'modal modal-lg' : 'modal';
  const guarded = !!confirmOverlayClose;
  // Клик считается кликом по подложке, только если и нажатие началось на ней:
  // иначе выделение текста в поле с отпусканием мыши снаружи закрывало окно.
  const pressedOnOverlay = useRef(false);
  // «Что-то вводили» = было событие ввода в поле формы. Пустую форму закрываем молча.
  const dirty = useRef(false);
  const dirtyPropRef = useRef(dirtyProp);
  dirtyPropRef.current = dirtyProp;

  useEffect(() => {
    dirty.current = false;
    if (!guarded) return undefined;
    const onBeforeUnload = (e) => {
      if (!dirty.current && !dirtyPropRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guarded]);

  const markDirty = () => { dirty.current = true; };

  const overlayRef = useRef(null);
  const requestCloseRef = useRef(null);
  // Escape — как клик в подложку (с вопросом, если вводили), и только у верхнего окна: вложенное
  // окно позже в DOM. Список, календарь, лайтбокс и nfConfirm забирают Escape раньше (UX-аудит 25.10)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const overlays = document.querySelectorAll('.modal-overlay');
      if (overlays[overlays.length - 1] !== overlayRef.current) return;
      e.preventDefault();
      requestCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const requestClose = async () => {
    if (guarded && (dirty.current || dirtyProp)) {
      const question = typeof confirmOverlayClose === 'string' ? confirmOverlayClose : DEFAULT_CLOSE_CONFIRM;
      const ok = await nfConfirm(question, { title: 'Закрыть форму?', confirmText: 'Закрыть', cancelText: 'Продолжить заполнение' });
      if (!ok) return;
    }
    onClose();
  };
  requestCloseRef.current = requestClose;

  const handleOverlayClick = async (e) => {
    const fromOverlay = pressedOnOverlay.current && e.target === e.currentTarget;
    pressedOnOverlay.current = false;
    if (!fromOverlay) return;
    if (guarded && (dirty.current || dirtyProp)) {
      const question = typeof confirmOverlayClose === 'string' ? confirmOverlayClose : DEFAULT_CLOSE_CONFIRM;
      const ok = await nfConfirm(question, { title: 'Закрыть форму?', confirmText: 'Закрыть', cancelText: 'Продолжить заполнение' });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => { pressedOnOverlay.current = e.target === e.currentTarget; }}
      onClick={handleOverlayClick}
    >
      <div className={modalClass} onClick={e => e.stopPropagation()} onInput={markDirty} onChange={markDirty}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
